import React from 'react';
import TrackingDetails from './TrackingDetails';
import { ArrowLeft24Filled } from '@fluentui/react-icons';
import { format } from 'date-fns';
import { zoneMeaningMap } from '../constants/zoneMeaning';
import {
  Printer,
  ScrollText,
  Copy,
  XCircle,
  AlertCircle,
  FileText,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../hooks/useModal';
import { useScreenSize } from '../hooks/useScreenSize';
import { Button } from './ui/Button';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { ordersService } from '../services/orders';
import { getShipmentStatusClasses } from '../utils/status';

interface CancellationModalContentProps {
  orderNumber: string;
  onCancelShipment: () => void;
  onCancelOrder: () => void;
  onClose: () => void;
}

const CancellationModalContent: React.FC<CancellationModalContentProps> = ({
  orderNumber,
  onCancelShipment,
  onCancelOrder,
  onClose,
}) => {
  return (
    <div className="space-y-4 p-4 text-center">
      {/* Red Warning triangle icon */}
      <svg
        className="w-16 h-16 text-red-600 mx-auto"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 3L2 21H22L12 3Z"
          fill="#EF4444"
          stroke="#EF4444"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 9V13"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 17H12.01"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <h3 className="text-xl font-bold text-gray-905 mt-4 leading-tight text-gray-900">
        Do you want to cancel the Order or Shipment?
      </h3>
      <p className="text-sm text-gray-500 font-medium -mt-2">
        You can't undo this action.
      </p>

      {/* Informational warning block (yellow container) */}
      <div className="bg-amber-50/70 border border-amber-100 rounded-lg p-4 mt-6 text-left">
        <div className="flex gap-3 text-sm">
          <svg
            className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div className="space-y-3 leading-relaxed text-amber-900 font-medium">
            <p>
              Cancellation request will be forwarded to the courier partner.
              Once the cancellation is confirmed by the partner, the freight
              charges will be refunded and credited to your Parcelpicks wallet
              immediately.
            </p>
          </div>
        </div>
      </div>

      {/* Buttons at the bottom */}
      <div className="flex gap-4 pt-4">
        <button
          className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg px-6 py-3 font-semibold transition-colors shadow-sm text-sm"
          onClick={() => {
            onCancelOrder();
          }}
        >
          Cancel Order
        </button>
        <button
          className="flex-1 bg-white hover:bg-red-50 text-red-600 border border-red-500 rounded-lg px-6 py-3 font-semibold transition-colors text-sm"
          onClick={() => {
            onClose();
          }}
        >
          Close Popup
        </button>
      </div>
    </div>
  );
};

export default function OrderDetails({ order, onBack }: any) {
  const navigate = useNavigate();
  const { open: openModal, close: closeModal, Modal } = useModal();
  const { isLargeScreen } = useScreenSize();
  const [shipmentType, setShipmentType] = React.useState<'forward' | 'rto'>(
    'forward'
  );
  const { user } = useAuth();
  const isAdmin = user?.roleName?.toLowerCase() === 'admin';
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [isGeneratingManifest, setIsGeneratingManifest] = React.useState(false);
  const [isGeneratingLabel, setIsGeneratingLabel] = React.useState(false);

  const [openSections, setOpenSections] = React.useState<
    Record<string, boolean>
  >({
    tracking: true,
    order: true,
    product: true,
    pickup: true,
    delivery: true,
    return: true,
    shipping: true,
    package: true,
    charges: true,
  });

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const Section = ({
    title,
    sectionKey,
    children,
  }: {
    title: string;
    sectionKey: string;
    children: React.ReactNode;
  }) => {
    const isOpen = openSections[sectionKey];

    return (
      <div className="bg-white rounded-lg border shadow-sm">
        <div
          onClick={() => toggleSection(sectionKey)}
          className="px-6 py-4 border-b cursor-pointer flex justify-between items-center"
        >
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <span>{isOpen ? '−' : '+'}</span>
        </div>

        {isOpen && <div className="p-6">{children}</div>}
      </div>
    );
  };

  React.useEffect(() => {
    if (!order.awb_number && order.rto_awb_number) {
      setShipmentType('rto');
    }
  }, [order.awb_number, order.rto_awb_number]);

  const normalizeZoneKey = (val?: string) => {
    if (!val) return null;
    return val.replace(/[-\s]/g, ' ').toUpperCase().trim();
  };

  const normalizeServiceName = (name?: string) => {
    if (!name) return '—';
    return name.replace(/\s*\(.*?\)\s*/g, '').trim();
  };

  const getZoneDisplay = (zone?: string) => {
    if (
      !zone ||
      zone === '—' ||
      zone.toUpperCase() === 'NA' ||
      zone.toUpperCase() === 'N/A'
    ) {
      return '—';
    }

    const trimmed = zone.trim();
    if (
      trimmed.includes(' - ') ||
      (trimmed.includes('Zone') &&
        (trimmed.includes('(') ||
          trimmed.includes('Within') ||
          trimmed.includes('Metro') ||
          trimmed.includes('India') ||
          trimmed.includes('Location')))
    ) {
      return trimmed;
    }

    const normalized = normalizeZoneKey(trimmed);
    const meaning =
      (normalized && zoneMeaningMap[normalized]) ||
      zoneMeaningMap[`ZONE ${trimmed.toUpperCase()}`] ||
      zoneMeaningMap[trimmed.toUpperCase()] ||
      '';

    const match = trimmed.match(/^(?:zone)?\s*[-_]?\s*(\d+|[a-zA-Z])$/i);
    let zoneNumber = trimmed;
    if (match) {
      zoneNumber = `Zone ${match[1].toUpperCase()}`;
    } else if (!trimmed.toLowerCase().startsWith('zone')) {
      zoneNumber = `Zone ${trimmed}`;
    }

    if (meaning) {
      return `${zoneNumber} - ${meaning}`;
    }

    return zoneNumber;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'dd MMM yyyy • hh:mm a');
    } catch {
      return '—';
    }
  };

  const isDummyAddressText = (text?: string | null) => {
    if (!text || typeof text !== 'string') return true;
    const lower = text.trim().toLowerCase();
    return (
      lower === 'address2 is not available' ||
      lower === 'address 2 is not available' ||
      lower === 'address2 not available' ||
      lower === 'address 2 not available' ||
      lower === 'address 2' ||
      lower === 'address2' ||
      lower === 'landmark is not available.' ||
      lower === 'landmark is not available' ||
      lower === 'landmark not available' ||
      lower === 'landmark' ||
      lower === 'not available' ||
      lower === 'not_available' ||
      lower === 'not applicable' ||
      lower === 'n/a' ||
      lower === 'na' ||
      lower === 'null' ||
      lower === 'none'
    );
  };

  const formatAddress = (addr: any) => {
    if (!addr) return '—';
    const parts = [
      addr.address1,
      addr.address2,
      addr.landmark,
      addr.pincode,
      addr.city,
      addr.state,
      addr.country,
    ]
      .map((part) =>
        typeof part === 'string'
          ? part.trim().replace(/^[,;\s]+|[,;\s]+$/g, '')
          : ''
      )
      .filter((part) => Boolean(part) && !isDummyAddressText(part));

    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const s = order.shipment_status?.toLowerCase() || '';
  const isReady =
    s.includes('ready') ||
    s.includes('pickup') ||
    s.includes('manifest') ||
    s.includes('label') ||
    s.includes('exception');
  const isInTransit = s.includes('transit') && !s.includes('rto');
  const isRTO = s.includes('rto');
  const isDelivered = s.includes('delivered');
  const isPending = s === 'pending';

  const showLabel = isReady || isPending || ((isInTransit || isRTO) && isAdmin);
  const showManifest = isReady || ((isInTransit || isRTO) && isAdmin);
  const showCancel =
    isReady || isPending || ((isInTransit || isRTO) && isAdmin);
  const showClone = true;

  const handleLabelClick = async () => {
    if (!order.shipment_id || !user?.id) {
      if (order.label_url) {
        window.open(order.label_url, '_blank');
        return;
      }
      toast.error('No shipment ID available for this order');
      return;
    }

    setIsGeneratingLabel(true);
    const toastId = toast.loading('Generating label...');
    try {
      const res = await ordersService.getZipLabels(user.id, {
        standard: [Number(order.shipment_id)],
        thermal: [],
      });
      const url =
        res.std_pdf ||
        res.ther_pdf ||
        res.url ||
        res.data ||
        res.data?.url ||
        (res.success && res.data);

      if (url) {
        window.open(url, '_blank');
        toast.success('Label generated successfully', { id: toastId });
      } else if (order.label_url) {
        window.open(order.label_url, '_blank');
      } else {
        throw new Error(res.message || 'Failed to generate label');
      }
    } catch (err: any) {
      if (order.label_url) {
        window.open(order.label_url, '_blank');
      } else {
        toast.error(err.message || 'Failed to generate label', { id: toastId });
      }
    } finally {
      setIsGeneratingLabel(false);
    }
  };

  const handleCloneOrder = () => {
    navigate('/orders/add', {
      state: {
        deliveryDetails: {
          companyName:
            order.destination_address?.company_name ||
            order.destination_address?.contact_name ||
            '',
          address1: order.destination_address?.address1 || '',
          address2: isDummyAddressText(order.destination_address?.address2)
            ? ''
            : order.destination_address?.address2 || '',
          landmark: isDummyAddressText(order.destination_address?.landmark)
            ? ''
            : order.destination_address?.landmark || '',
          pinCode: order.destination_address?.pincode || '',
          city: order.destination_address?.city || '',
          state: order.destination_address?.state || '',
          phoneNumber: order.destination_address?.contact_no || '',
          email: order.destination_address?.email || '',
          altContactNo: order.destination_address?.alt_contact_no || '',
        },
        orderDetails: {
          orderId: order.order_number || '',
          weight: String(order.dead_weight || ''),
          dimensions: {
            length: String(order.length || ''),
            breadth: String(order.breadth || ''),
            height: String(order.height || ''),
          },
        },
        products: [
          {
            productName: order.package_desc || '',
            quantity: 1,
            invoiceValue: String(order.shipment_value || ''),
            sku: '',
            hsn: '',
          },
        ],
        warehouse: String(
          order.pickup_id || order.origin_address?.pickup_id || ''
        ),
      },
    });
  };

  const handleManifestClick = async () => {
    if (!user?.id) return;
    setIsGeneratingManifest(true);
    const toastId = toast.loading('Generating manifest...');
    try {
      const res = await ordersService.getManifest(user.id, {
        shipmentData: {
          shipments: [
            {
              courierId: order.courier_id?.toString() || '1',
              shipmentIds: order.shipment_id?.toString() || '',
            },
          ],
        },
      });
      if ((res.success || res.url || res.data) && res.code !== 404) {
        toast.success('Manifest generated successfully', { id: toastId });
        if (res.url || res.data) {
          window.open(res.url || res.data, '_blank');
        }
      } else {
        throw new Error(res.message || 'Failed to generate manifest');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to generate manifest', {
        id: toastId,
      });
    } finally {
      setIsGeneratingManifest(false);
    }
  };

  const handleCancelClick = () => {
    openModal({
      element: (
        <CancellationModalContent
          orderNumber={order.order_number || ''}
          onCancelOrder={async () => {
            setIsCancelling(true);
            const tid = toast.loading('Cancelling shipment...');
            try {
              const res = await ordersService.cancelOrder(
                order.shipment_id,
                user?.id as string
              );
              if (res.success || res.code === 200) {
                toast.success('Shipment cancelled successfully', {
                  id: tid,
                });
                closeModal();
                onBack(); // Refresh or go back
              } else {
                throw new Error(res.message || 'Failed to cancel shipment');
              }
            } catch (err: any) {
              const errMsg =
                err.response?.data?.message ||
                err.message ||
                'Failed to cancel shipment';
              toast.error(errMsg, {
                id: tid,
              });
            } finally {
              setIsCancelling(false);
            }
          }}
          onCancelShipment={async () => {
            setIsCancelling(true);
            const tid = toast.loading('Cancelling shipment...');
            try {
              const res = await ordersService.cancelOrder(
                order.shipment_id,
                user?.id as string
              );
              if (res.success || res.code === 200) {
                toast.success('Shipment cancelled successfully', {
                  id: tid,
                });
                closeModal();
                onBack(); // Refresh or go back
              } else {
                throw new Error(res.message || 'Failed to cancel shipment');
              }
            } catch (err: any) {
              const errMsg =
                err.response?.data?.message ||
                err.message ||
                'Failed to cancel shipment';
              toast.error(errMsg, {
                id: tid,
              });
            } finally {
              setIsCancelling(false);
            }
          }}
          onClose={closeModal}
        />
      ),
      centered: true,
      closable: true,
      className: 'max-w-lg',
    });
  };

  const chargeableWeight =
    order.dead_weight && order.volumetric_weight
      ? Math.max(order.dead_weight, order.volumetric_weight)
      : (order.dead_weight ?? order.volumetric_weight ?? '—');

  return (
    <div className="h-screen flex flex-col overflow-hidden px-4 xl:px-0 py-6 xl:py-8">
      {/* Header */}
      <div className="flex-shrink-0 py-4">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 xl:gap-0">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft24Filled />
            </button>
            <div className="flex flex-col xl:flex-row xl:items-center gap-1 xl:gap-8">
              <div className="flex items-center gap-2 xl:gap-2">
                <h1 className="text-3xl xl:text-2xl font-bold text-text">
                  Order Details
                </h1>
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full border ${getShipmentStatusClasses(order.shipment_status)}`}
                >
                  {order.shipment_status ?? 'Pending'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row xl:items-center gap-4 xl:gap-10">
            <div className="flex flex-col xl:flex-row xl:items-center gap-3 xl:gap-6">
              <div className="flex flex-row gap-2 lg:gap-2">
                {showLabel && (
                  <button
                    disabled={isGeneratingLabel}
                    onClick={handleLabelClick}
                    className="flex items-center gap-1 px-3 py-2 text-gray-700 text-sm font-semibold hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors disabled:opacity-50"
                  >
                    <Printer className="h-4 w-4" />
                    <span>
                      {isGeneratingLabel ? 'Generating...' : 'Print Label'}
                    </span>
                  </button>
                )}
                {showManifest && (
                  <button
                    disabled={isGeneratingManifest}
                    onClick={handleManifestClick}
                    className="flex items-center gap-2 px-3 py-2 text-gray-700 text-sm font-semibold hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors disabled:opacity-50"
                  >
                    <ScrollText className="h-4 w-4" />
                    <span>
                      {isGeneratingManifest
                        ? 'Generating...'
                        : 'Print Manifest'}
                    </span>
                  </button>
                )}
                {showCancel && (
                  <button
                    onClick={handleCancelClick}
                    className="flex items-center gap-2 px-3 py-2 text-red-600 text-sm font-semibold hover:bg-red-50 rounded-lg border border-red-200 transition-colors"
                  >
                    <XCircle className="h-4 w-4" />
                    <span>Cancel Shipment</span>
                  </button>
                )}
                {showClone && (
                  <button
                    onClick={handleCloneOrder}
                    className="flex items-center gap-2 px-3 py-2 text-gray-700 text-sm font-semibold hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                    <span>Clone Order</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forward / RTO tabs — only if RTO awb exists */}
      {order.rto_awb_number && (
        <div className="flex mt-2 gap-8 border-b">
          <button
            onClick={() => setShipmentType('forward')}
            className={`pb-2 text-lg font-semibold transition-colors ${
              shipmentType === 'forward'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-400'
            }`}
          >
            Forward Shipment
          </button>
          <button
            onClick={() => setShipmentType('rto')}
            className={`pb-2 text-lg font-semibold transition-colors ${
              shipmentType === 'rto'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-400'
            }`}
          >
            Return Shipment (RTO)
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Left Column */}
        <div className="flex-1 overflow-y-auto pb-6 px-2 xl:px-0">
          <div className="mx-auto space-y-8">
            {/* Tracking History (Mobile Only) */}
            <div className="lg:hidden bg-white rounded-lg border shadow-sm">
              <div
                onClick={() => toggleSection('tracking')}
                className="px-6 py-4 border-b cursor-pointer flex justify-between items-center"
              >
                <h2 className="text-lg font-semibold text-gray-900">
                  Tracking Status
                </h2>
                <span>{openSections.tracking ? '−' : '+'}</span>
              </div>
              {openSections.tracking && !isLargeScreen && (
                <div className="p-0">
                  <TrackingDetails
                    trackingId={order.awb_number ?? ''}
                    rtoTrackingId={order.rto_awb_number ?? null}
                    orderStatus={order.shipment_status ?? ''}
                    embedded={true}
                  />
                </div>
              )}
            </div>

            {/* Order Details */}
            <Section title="Order Details" sectionKey="order">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-base">
                <div>
                  <p className=" mb-1 font-semibold">Channel order date</p>
                  <p className="text-gray-500 font-medium leading-tight">
                    {formatDate(order.created_at)
                      .split(' • ')
                      .map((part, i) => (
                        <span key={i} className="block">
                          {part}
                        </span>
                      ))}
                  </p>
                </div>
                <div>
                  <p className=" mb-1 font-semibold">Channel</p>
                  <p className="text-gray-500 font-medium">
                    {order.shop_name ?? 'Manual'}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-semibold">Order Type</p>
                  <div className="flex gap-2">
                    <span
                      className={`px-2 py-0.5 text-xs font-bold rounded border ${order.payment_mode != 'ONLINE' ? 'text-red-600 border-red-600 bg-red-50' : 'text-green-600 bg-green-50 border-green-600]'}`}
                    >
                      {order.payment_mode === 'ONLINE' ? 'Prepaid' : 'COD'}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="mb-1 font-semibold">Order Value</p>
                  <p className="font-medium text-gray-500">
                    ₹{order.shipment_value ?? '0'}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-semibold">Shipment Id</p>
                  <p className="font-medium text-gray-500">
                    {order.shipment_id ?? '—'}
                  </p>
                </div>

                <div>
                  <p className="mb-1 font-semibold">Order Schedule date</p>
                  <p className="text-gray-500 font-medium leading-tight">
                    {formatDate(order.created_at)
                      .split(' • ')
                      .map((part, i) => (
                        <span key={i} className="block">
                          {part}
                        </span>
                      ))}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-semibold">Product HSN</p>
                  <p className="font-medium text-gray-500">
                    {order.hsn ?? 'NA'}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-semibold">Product SKU</p>
                  <p className="font-medium text-gray-500">
                    {order.sku ?? 'NA'}
                  </p>
                </div>
              </div>
            </Section>

            {/* Product Details */}
            <Section title="Product Details" sectionKey="product">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b">
                      <th className="pb-3 px-2 font-bold text-gray-800 w-1/2">
                        Name
                      </th>
                      <th className="pb-3 px-2 font-bold text-gray-800 w-32">
                        SKU
                      </th>
                      <th className="pb-3 px-2 font-bold text-gray-800 w-24">
                        HSN
                      </th>
                      <th className="pb-3 px-2 font-bold text-gray-800 text-center w-20">
                        Qty
                      </th>
                      <th className="pb-3 px-2 font-bold text-gray-800 text-right w-32">
                        Product Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {order.products && order.products.length > 0 ? (
                      order.products.map((p: any, idx: number) => (
                        <tr key={idx}>
                          <td className="py-4 px-2 text-gray-700 text-sm">
                            {p.productName}
                          </td>
                          <td className="py-4 px-2 text-gray-700 text-sm">
                            {p.sku || 'NA'}
                          </td>
                          <td className="py-4 px-2 text-gray-700 text-sm">
                            {p.hsn || 'NA'}
                          </td>
                          <td className="py-4 px-2 text-gray-700 text-sm text-center">
                            {p.quantity || 1}
                          </td>
                          <td className="py-4 px-2 text-gray-700 text-sm text-right font-medium">
                            ₹{p.invoiceValue}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="py-4 px-2 text-gray-700 text-sm">
                          {order.package_desc || '—'}
                        </td>
                        <td className="py-4 px-2 text-gray-700 text-sm">
                          {order.sku || 'NA'}
                        </td>
                        <td className="py-4 px-2 text-gray-700 text-sm">NA</td>
                        <td className="py-4 px-2 text-gray-700 text-sm text-center">
                          {order.package_detail?.no_of_items ||
                            order.quantity ||
                            1}
                        </td>
                        <td className="py-4 px-2 text-gray-700 text-sm text-right font-medium">
                          ₹{order.shipment_value || '0'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-100">
                      <td
                        colSpan={4}
                        className="py-4 px-2 text-right font-bold text-gray-900 border-r"
                      >
                        Order Value
                      </td>
                      <td className="py-4 px-2 text-right font-bold text-gray-900">
                        ₹{order.shipment_value || '0'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Section>

            {/* Pickup Details */}
            <Section title="Pickup Details" sectionKey="pickup">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-base mb-4">
                <div>
                  <p className="text-gray-800 mb-1 font-bold">Name</p>
                  <p className="text-gray-700">
                    {order.origin_address?.contact_name ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-800 mb-1 font-bold">Number</p>
                  <p className="text-gray-700">
                    {order.origin_address?.contact_no ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-800 mb-1 font-bold">Email</p>
                  <p className="text-gray-700 break-all">
                    {order.origin_address?.email ?? '—'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-gray-800 mb-1 font-bold">Address</p>
                <p className="text-gray-700 leading-relaxed text-sm">
                  {formatAddress(order.origin_address)}
                </p>
              </div>
            </Section>

            {/* Delivery Details */}
            <Section title="Delivery Details" sectionKey="delivery">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-base mb-4">
                <div>
                  <p className="text-gray-800 mb-1 font-bold">Name</p>
                  <p className="text-gray-700">
                    {order.destination_address?.contact_name ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-800 mb-1 font-bold">Number</p>
                  <p className="text-gray-700">
                    {order.destination_address?.contact_no ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-800 mb-1 font-bold">Email</p>
                  <p className="text-gray-700 break-all">
                    {order.destination_address?.email ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-800 mb-1 font-bold">Alt. Number</p>
                  <p className="text-gray-700">
                    {order.destination_address?.alt_contact_no ?? '—'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-gray-800 mb-1 font-bold">Address</p>
                <p className="text-gray-700 leading-relaxed text-sm">
                  {formatAddress(order.destination_address)}
                </p>
              </div>
            </Section>

            {/* Return Address */}
            {/* {order.return_address && (
              <div className="bg-white rounded-lg border shadow-sm">
                <div
                  onClick={() => toggleSection('return')}
                  className="px-6 py-4 border-b cursor-pointer flex justify-between items-center"
                >
                  <h2 className="text-lg font-semibold text-gray-900">
                    Return/RTO Details
                  </h2>
                  <span>{openSections.return ? '−' : '+'}</span>
                </div>
                {openSections.return && (
                  <div className="p-6">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 text-base">
                      <div>
                        <p className="text-gray-500 mb-1">Name</p>
                        <p className="font-medium">
                          {order.return_address?.contact_name ?? '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Contact</p>
                        <p className="font-medium">
                          {order.return_address?.contact_no ?? '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Email</p>
                        <p className="font-medium">
                          {order.return_address?.email ?? '—'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 text-base">
                      <p className="text-gray-500 mb-1">Address</p>
                      <p className="font-medium leading-relaxed">
                        {formatAddress(order.return_address)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )} */}

            {/* Shipping Details */}
            {order.awb_number && (
              <Section title="Shipping Details" sectionKey="shipping">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-base">
                  <div>
                    <p className="text-gray-800 mb-1 font-bold">Courier Name</p>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700">
                        {order.courier_name ?? '—'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-800 mb-1 font-bold">AWB Number</p>
                    <p className="text-gray-700">{order.awb_number ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-800 mb-1 font-bold">Mode</p>
                    <p className="text-gray-700">
                      {order.service_name ?? 'Surface'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-800 mb-1 font-bold">
                      Expected Pickup
                    </p>
                    <p className="text-gray-700">
                      {formatDate(order.shipment_status_date)}
                    </p>
                  </div>
                </div>
              </Section>
            )}

            {/* Package Details */}
            <Section title="Package Details" sectionKey="package">
              {(() => {
                const parsePkg = (val: any) => {
                  if (!val) return null;
                  try {
                    return typeof val === 'string' ? JSON.parse(val) : val;
                  } catch (e) {
                    return null;
                  }
                };

                const pd =
                  parsePkg((order as any).package_detail) ||
                  parsePkg((order as any).package_details);
                const prd =
                  parsePkg((order as any).package_rate_details) ||
                  parsePkg((order as any).rate_details);

                const extraWeight =
                  order.extra_weight ??
                  pd?.extra_weight ??
                  prd?.extra_weight ??
                  null;
                const courierChargedWeight =
                  order.courier_charged_weight ??
                  pd?.courier_charged_weight ??
                  prd?.courier_charged_weight ??
                  null;

                const hasOvercharge =
                  (extraWeight != null && Number(extraWeight) > 0) ||
                  (courierChargedWeight != null &&
                    Number(courierChargedWeight) > 0);

                return (
                  <div
                    className={`grid grid-cols-2 ${hasOvercharge ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6 text-base`}
                  >
                    <div>
                      <p className="text-gray-800 mb-1 font-bold">
                        Dead Weight
                      </p>
                      <p className="text-gray-700">
                        {order.dead_weight ?? '—'} Kg
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-800 mb-1 font-bold">
                        Dimensions (in cm)
                      </p>
                      <p className="text-gray-700">
                        {order.length ?? '—'} x {order.breadth ?? '—'} x{' '}
                        {order.height ?? '—'} cm
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-800 mb-1 font-bold">
                        Volumetric Weight (in kg)
                      </p>
                      <p className="text-gray-700">
                        {order.volumetric_weight ?? '—'} Kg
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-800 mb-1 font-bold">
                        Chargeable Weight (in kg)
                      </p>
                      <p className="text-gray-700">{chargeableWeight} Kg</p>
                    </div>
                    {hasOvercharge && (
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">
                          Courier Charged Weight (in kg)
                        </p>
                        <div>
                          <p className="text-gray-700">
                            {courierChargedWeight ?? '—'} Kg
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </Section>

            {/* Shipping Charges */}
            {order.awb_number && (
              <Section title="Shipping Charges" sectionKey="charges">
                {(() => {
                  let rateDetails: any = order.rate_details;
                  try {
                    while (typeof rateDetails === 'string') {
                      rateDetails = JSON.parse(rateDetails);
                    }
                  } catch (e) {
                    // ignore
                  }

                  let courierRateDetails: any = (order as any)
                    .courier_rate_details;
                  try {
                    while (typeof courierRateDetails === 'string') {
                      courierRateDetails = JSON.parse(courierRateDetails);
                    }
                  } catch (e) {
                    // ignore
                  }

                  const parsePkg = (val: any) => {
                    if (!val) return null;
                    try {
                      return typeof val === 'string' ? JSON.parse(val) : val;
                    } catch (e) {
                      return null;
                    }
                  };

                  const pd =
                    parsePkg((order as any).package_detail) ||
                    parsePkg((order as any).package_details);
                  const prd = parsePkg((order as any).package_rate_details);

                  const extraWeight =
                    order.extra_weight ??
                    pd?.extra_weight ??
                    prd?.extra_weight ??
                    null;
                  const courierChargedWeight =
                    order.courier_charged_weight ??
                    pd?.courier_charged_weight ??
                    prd?.courier_charged_weight ??
                    null;
                  const hasOvercharge =
                    (extraWeight != null && Number(extraWeight) > 0) ||
                    (courierChargedWeight != null &&
                      Number(courierChargedWeight) > 0);

                  let overchargedRate: any =
                    (order as any).overcharged_amount ??
                    pd?.overcharged_amount ??
                    null;
                  if (
                    overchargedRate == null &&
                    courierRateDetails?.total_charge != null &&
                    rateDetails?.total_charge != null
                  ) {
                    const diff =
                      Number(courierRateDetails.total_charge) -
                      Number(rateDetails.total_charge);
                    if (diff > 0) overchargedRate = diff;
                  }
                  if (
                    overchargedRate == null &&
                    hasOvercharge &&
                    rateDetails?.user_price_zone_slab != null &&
                    extraWeight != null
                  ) {
                    try {
                      const upz = rateDetails.user_price_zone_slab;
                      const addlSlab = upz.addlWeightSlab || 0.5;
                      const baseAddl = upz.baseRateAddl || upz.baseRate || 0;
                      const multFactor = Math.ceil(
                        Number(extraWeight) / addlSlab
                      );
                      const extraBase = multFactor * baseAddl;
                      const fuelPerc =
                        (upz.fuelSurcharge || 0) +
                        multFactor * (upz.fuelSurchargeAddl || 0);
                      const extraFuel = (extraBase * fuelPerc) / 100;
                      const extraSubtotal = extraBase + extraFuel;
                      const taxPerc = upz.serviceTax || 18.0;
                      const extraTax = (extraSubtotal * taxPerc) / 100;
                      overchargedRate = Number(
                        (extraSubtotal + extraTax).toFixed(2)
                      );
                    } catch (e) {
                      // ignore
                    }
                  }

                  const formatAmount = (val: any) => {
                    if (val == null || val === '' || isNaN(Number(val)))
                      return '0.00';
                    return Number(val).toFixed(2);
                  };

                  const baseCharge =
                    rateDetails?.base_rate ?? order.base_freight ?? '0';
                  const codCharge =
                    rateDetails?.COD_charges ?? order.cod_charge ?? '0';
                  const insuranceCharge = '0';
                  const dphCharge = rateDetails?.other_charges ?? '0';
                  const gstCharge =
                    rateDetails?.service_tax ?? order.gst_charge ?? '0';
                  const totalFreight =
                    rateDetails?.total_charge ?? order.shipping_cost ?? '0';

                  const paymentMode = order.payment_mode?.toUpperCase() || '';
                  const isPrepaid =
                    paymentMode === 'ONLINE' || paymentMode === 'PREPAID';

                  const showOverchargedRate =
                    hasOvercharge &&
                    overchargedRate != null &&
                    Number(overchargedRate) > 0;
                  const zoneVal =
                    order.zone ||
                    rateDetails?.user_price_zone_slab?.zone ||
                    rateDetails?.zone;

                  return (
                    <div
                      className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 ${showOverchargedRate ? (!isPrepaid ? 'lg:grid-cols-9' : 'lg:grid-cols-8') : !isPrepaid ? 'lg:grid-cols-8' : 'lg:grid-cols-7'} gap-6 text-base`}
                    >
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">Zone</p>
                        <p className="text-gray-700">
                          {getZoneDisplay(zoneVal)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">Slab</p>
                        <p className="text-gray-700">
                          {order.base_weight_slab ?? '—'} Kg
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">
                          Base Charge
                        </p>
                        <p className="text-gray-700">
                          ₹{formatAmount(baseCharge)}
                        </p>
                      </div>
                      {!isPrepaid && (
                        <div>
                          <p className="text-gray-800 mb-1 font-bold">COD</p>
                          <p className="text-gray-700">
                            ₹{formatAmount(codCharge)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">
                          Insurance
                        </p>
                        <p className="text-gray-700">
                          ₹{formatAmount(insuranceCharge)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">DPH</p>
                        <p className="text-gray-700">
                          ₹{formatAmount(dphCharge)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">GST</p>
                        <p className="text-gray-700">
                          ₹{formatAmount(gstCharge)}
                        </p>
                      </div>
                      {showOverchargedRate && (
                        <div>
                          <p className="text-gray-800 mb-1 font-bold">
                            Overcharged Rate
                          </p>
                          <p className="text-gray-700">
                            ₹{formatAmount(overchargedRate)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-800 mb-1 font-bold">
                          Total Freight
                        </p>
                        <p className="text-gray-900 font-bold">
                          ₹{formatAmount(totalFreight)}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </Section>
            )}
          </div>
        </div>

        {/* Right Column - Tracking */}
        <div className="max-w-xl hidden lg:block lg:my-6 rounded-lg bg-white overflow-y-auto">
          <div className="p-6">
            {isLargeScreen && (
              <TrackingDetails
                trackingId={order.awb_number ?? ''}
                rtoTrackingId={order.rto_awb_number ?? null}
                orderStatus={order.shipment_status ?? ''}
              />
            )}
          </div>
        </div>
      </div>
      <Modal />
    </div>
  );
}
